import { lumi } from '@/lib/lumi'

/**
 * Prefijo usado para identificar las notificaciones internas de revisión
 * por perfil duplicado, de modo que el panel admin pueda filtrarlas.
 */
export const DUPLICATE_ALERT_PREFIX = '[Posible duplicado]'

interface CompanyDupInput {
  userId: string
  tradeName?: string
  rfc?: string
  email?: string
}

interface CollaboratorDupInput {
  userId: string
  fullName?: string
  email?: string
  phone?: string
}

const norm = (v?: string) => (v || '').trim().toLowerCase()

/**
 * Crea una notificación dirigida al administrador para que revise un posible
 * perfil duplicado y, en su caso, anule el registro.
 */
async function notifyAdminDuplicate(params: {
  title: string
  body: string
  linkUrl: string
  creator: string
}) {
  const now = new Date().toISOString()
  try {
    await lumi.entities.notifications.create({
      title: `${DUPLICATE_ALERT_PREFIX} ${params.title}`,
      body: params.body,
      type: 'aviso',
      targetAudience: 'admin',
      status: 'publicada',
      readBy: [],
      linkUrl: params.linkUrl,
      creator: params.creator,
      createdAt: now,
      updatedAt: now,
    })
  } catch (e) {
    console.error('[duplicateDetection] notifyAdminDuplicate error:', e)
  }
}

/**
 * Detecta posibles empresas duplicadas comparando RFC, nombre comercial o correo
 * contra los registros existentes (excluyendo el propio usuario). Si encuentra
 * coincidencias, notifica al admin para revisión/anulación.
 */
export async function checkCompanyDuplicate(input: CompanyDupInput) {
  const tradeName = norm(input.tradeName)
  const rfc = norm(input.rfc)
  const email = norm(input.email)
  if (!tradeName && !rfc && !email) return

  try {
    const res = await lumi.entities.companies.list({ limit: 500 })
    const matches = (res.list as Array<Record<string, unknown>>).filter((c) => {
      if (c.userId === input.userId) return false
      const cTrade = norm(c.tradeName as string)
      const cRfc = norm(c.rfc as string)
      const cEmail = norm(c.email as string)
      return (
        (!!rfc && cRfc === rfc) ||
        (!!tradeName && cTrade === tradeName) ||
        (!!email && cEmail === email)
      )
    })

    if (matches.length === 0) return

    const reasons: string[] = []
    if (rfc && matches.some((m) => norm(m.rfc as string) === rfc)) reasons.push(`RFC "${input.rfc}"`)
    if (tradeName && matches.some((m) => norm(m.tradeName as string) === tradeName)) reasons.push(`nombre comercial "${input.tradeName}"`)
    if (email && matches.some((m) => norm(m.email as string) === email)) reasons.push(`correo "${input.email}"`)

    await notifyAdminDuplicate({
      title: `Empresa duplicada: ${input.tradeName || input.email || 'sin nombre'}`,
      body: `Se detectó una posible empresa duplicada por coincidencia de ${reasons.join(', ')}. Existen ${matches.length} registro(s) similar(es). Revisa y, en su caso, anula el registro duplicado.`,
      linkUrl: '/admin/empresas',
      creator: input.userId,
    })
  } catch (e) {
    console.error('[duplicateDetection] checkCompanyDuplicate error:', e)
  }
}

/**
 * Detecta posibles colaboradores duplicados comparando correo, teléfono o nombre
 * completo contra los registros existentes (excluyendo el propio usuario). Si
 * encuentra coincidencias, notifica al admin para revisión/anulación.
 */
export async function checkCollaboratorDuplicate(input: CollaboratorDupInput) {
  const fullName = norm(input.fullName)
  const email = norm(input.email)
  const phone = norm(input.phone)
  if (!fullName && !email && !phone) return

  try {
    const res = await lumi.entities.collaborators.list({ limit: 500 })
    const matches = (res.list as Array<Record<string, unknown>>).filter((c) => {
      if (c.userId === input.userId) return false
      const cName = norm(c.fullName as string)
      const cEmail = norm(c.email as string)
      const cPhone = norm(c.phone as string)
      return (
        (!!email && cEmail === email) ||
        (!!phone && cPhone === phone) ||
        (!!fullName && cName === fullName)
      )
    })

    if (matches.length === 0) return

    const reasons: string[] = []
    if (email && matches.some((m) => norm(m.email as string) === email)) reasons.push(`correo "${input.email}"`)
    if (phone && matches.some((m) => norm(m.phone as string) === phone)) reasons.push(`teléfono "${input.phone}"`)
    if (fullName && matches.some((m) => norm(m.fullName as string) === fullName)) reasons.push(`nombre "${input.fullName}"`)

    await notifyAdminDuplicate({
      title: `Colaborador duplicado: ${input.fullName || input.email || 'sin nombre'}`,
      body: `Se detectó un posible colaborador duplicado por coincidencia de ${reasons.join(', ')}. Existen ${matches.length} registro(s) similar(es). Revisa y, en su caso, anula el registro duplicado.`,
      linkUrl: '/admin/colaboradores',
      creator: input.userId,
    })
  } catch (e) {
    console.error('[duplicateDetection] checkCollaboratorDuplicate error:', e)
  }
}
